import {
  ColDef,
  EditableCallbackParams,
  GridApi,
  GridOptions,
  RowNode,
  SortChangedEvent,
} from '@ag-grid-community/core'
import {
  InlineEditDataType,
  NgGridComponents,
} from '@northfork/ag-grid-angular'
import { parseDate } from '@northfork/basics'
import { isAfter } from 'date-fns'
import {
  BulkTransactionModalInput,
  BulkTransactionModalMode,
} from '../model/bulk-transaction/bulk-popup-input-model'
import { MvtBasis, MvtBasisSelection } from '../model/mvt-basis.model'
import { PricingRequest } from '../model/pricing.model'
import { RequestMode } from '../model/validation-response.model'
import { isBulkEditModal, isBulkExternalModal } from './bulk-popup-input-utils'
import { TransactionConstants } from './constants'

/**
 * Add error invalidation  to a transaction row
 */
export function addErrorDetails(
  rowNode: RowNode,
  errorMessageMap: Map<string, string[]>,
) {
  if (!rowNode) return
  const data = rowNode.data ?? {}
  rowNode.setDataValue('validityStatus', 'invalid')
  rowNode.setDataValue('errorMessageMap', errorMessageMap)
  rowNode.setData(data)
}

/**
 * Add warning invalidation  to a transaction row
 */
export function addWarningDetails(
  rowNode: RowNode,
  errorMessageMap: Map<string, string[]>,
) {
  if (!rowNode) return
  const data = rowNode.data ?? {}
  rowNode.setDataValue('validityStatus', 'warning')
  rowNode.setDataValue('errorMessageMap', errorMessageMap)
  rowNode.setData(data)
}

/**
 * Remove invalidation i.e warning or error from a transaction row
 */
export function removeInvalidation(rowNode: RowNode) {
  if (!rowNode) return
  const data = rowNode.data ?? {}
  rowNode.setDataValue('validityStatus', 'valid')
  rowNode.setDataValue('errorMessageMap', new Map())
  rowNode.setData(data)
}

/**
 * handle rowNode Validation
 */
export function handleValidation(
  rowNode: RowNode,
  validationState: string,
  validationMessageMap: Map<string, string[]>,
) {
  switch (validationState) {
    case 'VALID':
      removeInvalidation(rowNode)
      break
    case 'WARNING':
      addWarningDetails(rowNode, validationMessageMap)
      break
    case 'INVALID':
      addErrorDetails(rowNode, validationMessageMap)
      break
  }
}

/**
 * Function for External CashWire mapping
 */
export function isDateCleared(postDate: string) {
  if (postDate && isAfter(new Date(), parseDate(postDate))) {
    return true
  } else {
    return false
  }
}

/**
 * Function to get bulkRequestMode
 */
export function bulkRequestMode(
  requestMode: BulkTransactionModalMode | undefined,
) {
  switch (requestMode) {
    case BulkTransactionModalMode.EditCWTran: {
      return RequestMode.EDIT_TRAN
    }
    case BulkTransactionModalMode.CreateCWTran: {
      return RequestMode.NEW_TRAN
    }
    case BulkTransactionModalMode.CreateFromExtTran: {
      return RequestMode.ENTER_EXTERNAL_TRAN
    }
    default: {
      return RequestMode.NEW_TRAN
    }
  }
}

/**
 * fill handle double click handler
 */
export function fillHandleDoubleClickHandler(api: GridApi) {
  // get selection range
  const ranges = api.getCellRanges()
  if (!ranges || ranges.length == 0) {
    return
  }
  const range = ranges[0]
  // if more than 1 row leave
  if (range.startRow?.rowIndex != range.endRow?.rowIndex) {
    return
  }
  // copy values in all active rows that is rows with data below the range of columns and 1 row
  const startRowIndex = range.startRow ? range.startRow?.rowIndex : 0
  const targetNodes: RowNode[] = []
  api.forEachNode(node => targetNodes.push(node))

  for (const rowNodeEffected of targetNodes) {
    if (rowNodeEffected.data.validityStatus == 'inactive') break
    if (
      rowNodeEffected.rowIndex &&
      Number(rowNodeEffected.rowIndex) > startRowIndex
    )
      range.columns
        .filter(
          column =>
            column.isCellEditable(rowNodeEffected) ||
            column.getColId() == 'mvtBasisSelectionText',
        )
        .forEach(column => {
          const value =
            api.getDisplayedRowAtIndex(startRowIndex)?.data[column.getColId()]
          api.getRowNode(rowNodeEffected.id ?? '')?.setDataValue(column, value)
        })
  }
}

/**
 * setUpNumberFormatter with current locale and minimum maximum fraction digits
 */
export function numberValueFormatter(
  numberToFormat: number,
  minimumFractionDigitsExpected: number,
  maximumFractionDigitsExpected: number,
) {
  return numberToFormat.toLocaleString(undefined, {
    minimumFractionDigits: minimumFractionDigitsExpected,
    maximumFractionDigits: maximumFractionDigitsExpected,
  })
}

/**
 * set rowNumber back to sequential order after sorting
 */
export function onSortChange(params: SortChangedEvent) {
  params.api.forEachNodeAfterFilterAndSort((rowNode, index) => {
    rowNode.data.rowNumber = (index + 1).toString()
  })
  params.api.redrawRows()
}
/**
 * add tranId column based on BulkTransactionModalMode
 */
export function showTranIdColumn(
  gridOptions: GridOptions,
  transInput: BulkTransactionModalInput,
  sortable: boolean,
) {
  const colDef = gridOptions.columnDefs
  if (!colDef) return
  const legacyOrigTranIdColumn: ColDef = {
    field: 'legacyOrigTranId',
    headerName: 'Orig Transaction ID',
    sortable: sortable,
    comparator: customSortComparatorBulkNumeric,
  }
  const externalTranIdColumn: ColDef = {
    field: 'externalTranId',
    headerName: 'Ext Transaction ID',
    sortable: sortable,
    comparator: customSortComparatorBulkNumeric,
  }
  if (transInput.modalMode == BulkTransactionModalMode.EditCWTran)
    colDef.splice(2, 0, legacyOrigTranIdColumn)
  else if (transInput.modalMode == BulkTransactionModalMode.CreateFromExtTran)
    colDef.splice(2, 0, externalTranIdColumn)
}

/**
 * add orderId column in Bulk Modals
 */
export function showOrderIDColumn(gridOptions: GridOptions, sortable: boolean) {
  const colDef = gridOptions.columnDefs
  if (!colDef) return
  colDef.push({
    field: 'orderId',
    headerName: 'Order ID',
    sortable: sortable,
    comparator: customSortComparatorBulkNumeric,
  })
}

// eslint-disable-next-line jsdoc/require-jsdoc
export function customSortComparatorBulk(
  valueA: string,
  valueB: string,
  nodeA: RowNode,
  nodeB: RowNode,
  isDescending: boolean,
) {
  // Check if nodeA or nodeB has a certain flag or condition that you want to sort on
  const rowFirstInactive = nodeA.data.validityStatus == 'inactive'
  const rowSecondInactive = nodeB.data.validityStatus == 'inactive'
  const valueNodeA = valueA ? valueA.toString() : ''
  const valueNodeB = valueB ? valueB.toString() : ''
  if (rowFirstInactive && !rowSecondInactive) {
    return !isDescending ? 1 : -1
  } else if (!rowFirstInactive && rowSecondInactive) {
    return !isDescending ? -1 : 1
  } else {
    // No special sorting, sort normally
    return valueNodeA.localeCompare(valueNodeB)
  }
}

// eslint-disable-next-line jsdoc/require-jsdoc
export function customSortComparatorBulkNumeric(
  valueA: string,
  valueB: string,
  nodeA: RowNode,
  nodeB: RowNode,
  isDescending: boolean,
) {
  // Check if nodeA or nodeB has a certain flag or condition that you want to sort on
  const rowFirstInactive = nodeA.data.validityStatus == 'inactive'
  const rowSecondInactive = nodeB.data.validityStatus == 'inactive'
  const valueNodeA = valueA ? Number(valueA) : 0
  const valueNodeB = valueB ? Number(valueB) : 0
  if (rowFirstInactive && !rowSecondInactive) {
    return !isDescending ? 1 : -1
  } else if (!rowFirstInactive && rowSecondInactive) {
    return !isDescending ? -1 : 1
  } else {
    // No special sorting, sort normally
    return valueNodeA - valueNodeB
  }
}

// eslint-disable-next-line jsdoc/require-jsdoc
export function customSortComparatorBulkValidatorColumn(
  valueA: string,
  valueB: string,
  nodeA: RowNode,
  nodeB: RowNode,
  isDescending: boolean,
) {
  // Check if nodeA or nodeB has a certain flag or condition that you want to sort on
  const rowFirstInactive = nodeA.data.validityStatus == 'inactive'
  const rowSecondInactive = nodeB.data.validityStatus == 'inactive'
  const valueNodeA = TransactionConstants.validityStatusPriority.indexOf(
    nodeA.data.validityStatus,
  )
  const valueNodeB = TransactionConstants.validityStatusPriority.indexOf(
    nodeB.data.validityStatus,
  )
  if (rowFirstInactive && !rowSecondInactive) {
    return !isDescending ? 1 : -1
  } else if (!rowFirstInactive && rowSecondInactive) {
    return !isDescending ? -1 : 1
  } else {
    // No special sorting, sort normally
    return valueNodeA - valueNodeB
  }
}

/**
 *  Log bulk dialog metrics
 */
export function bulkMetricsDetail(
  rowCount: number,
  transInput: BulkTransactionModalInput | undefined,
) {
  let type = 'Create'
  let count = 0
  if (isBulkEditModal(transInput)) {
    type = 'Edit'
    count = rowCount
  } else if (isBulkExternalModal(transInput)) {
    type = 'External'
    count = rowCount
  }
  return [count, type]
}

/**
 * convertBulkResponseKeyToPricingRequest
 */
export function convertBulkResponseKeyToPricingRequest(
  bulkResponseKey: string,
): PricingRequest {
  //"PricingRequest(accountId=4, cusip=26442CAP9, entryDate=2022-10-24)"
  bulkResponseKey = bulkResponseKey.replace('PricingRequest(', '')
  bulkResponseKey = bulkResponseKey.replace(')', '')
  bulkResponseKey = bulkResponseKey.replace('cusip=', '')
  bulkResponseKey = bulkResponseKey.replace('cusip=', '')
  bulkResponseKey = bulkResponseKey.replace('entryDate=', '')
  bulkResponseKey = bulkResponseKey.replace('accountId=', '')
  const bulkValues = bulkResponseKey.split(',')
  const cusip = bulkValues[1].trim()
  const entryDate = bulkValues[2].trim()
  const accountId = Number(bulkValues[0].trim())
  return {
    accountId,
    cusip,
    entryDate,
  }
}

/**
 * buildMvtBasisSelection for popup in transfers
 */
export function buildMvtBasisSelection(
  accountId: number,
  mvtBasis: MvtBasis,
  isSingleSidedTransfer: boolean,
  destinationAccountId?: number,
): MvtBasisSelection[] {
  const inBasis = mvtBasis.mvtBases?.allInBases
  const outBasis = mvtBasis.mvtBases?.allOutBases
  return mvtBasis.basis.map(base => {
    const accounts: number[] = []
    if (inBasis?.find(inBase => inBase.prefix == base.prefix)) {
      accounts.push(accountId)
    }
    if (
      destinationAccountId &&
      outBasis?.find(outBase => outBase.prefix == base.prefix)
    ) {
      accounts.push(destinationAccountId)
    }
    return {
      basis: base,
      accounts: isSingleSidedTransfer ? [accountId] : accounts,
      bookValue: isSingleSidedTransfer ? false : !base.checked,
      marketValue: isSingleSidedTransfer ? true : base.checked,
    }
  })
}

/**
 * Function to set Cell Params for AccountId Column in Bulk Dialogs
 */
export function getAccountCellParams(
  param: EditableCallbackParams,
  isOpenedFromBulkExTTransPanel: boolean,
  accountType?: 'simple' | 'aggregate' | 'sleeveSimple' | 'sleeveAggregate',
) {
  if ('sleeveAggregate' == accountType || isOpenedFromBulkExTTransPanel) {
    return {
      component: NgGridComponents.inlineEditor,
      params: {
        dataType: InlineEditDataType.Select,
        options: {
          items: param.data.sleeveSimpleAccountIds,
          bindValue: 'displayLabel',
          bindLabel: 'displayLabel',
        },
        isPopup: true,
      },
    }
  } else {
    return {
      component: NgGridComponents.inlineEditor,
      params: {
        dataType: InlineEditDataType.Number,
      },
      comparator: customSortComparatorBulkNumeric,
    }
  }
}

/**
 * Function to check if  row is empty
 */
export function isEmptyRow(rowNode: RowNode, excludedFields: string[]) {
  // Check if all other fields are empty
  return Object.entries(rowNode.data)
    .filter(([key]) => !excludedFields.includes(key))
    .every(([, value]) => {
      // Check if value is empty (empty string, null, undefined)
      return value === '' || value === null || value === undefined
    })
}

export const BULK_DIALOG_EVENT_NAME = 'heliosBulkDialogLoadTime'
